import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaRegHeart,
  FaHandHoldingHeart,
  FaUsers,
  FaBullhorn,
  FaBullseye,
  FaCommentDots,
  FaGlobe,
  FaChalkboardTeacher,
} from "react-icons/fa";
import "./ChildrensParliament.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const ChildrensParliament = () => {
  const objectives = [
    {
      icon: FaCommentDots,
      title: "Discuss & Understand",
      text: "Discuss problems and perspectives on equitable quality education.",
    },
    {
      icon: FaGlobe,
      title: "Promote Rights",
      text: "Highlight the UN Convention on the Rights of the Child.",
    },
    {
      icon: FaUsers,
      title: "Create Opportunities",
      text: "Provide a platform for children to discuss their rights and opportunities.",
    },
    {
      icon: FaChalkboardTeacher,
      title: "Mentor & Empower",
      text: "Mentor children to advocate for equitable education for all.",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (rebuilt to 1521 x 482, matching your other pages) ================= */}
      <section className="cp-page">
        <div className="cp-container">
          <motion.div
            className="cp-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="cp-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/Children%27s-Parliament.png)" }}
            />
            <div className="cp-hero__overlay" />

            <div className="cp-hero__content">
              <div className="cp-hero__mark">
                <FaRegHeart className="cp-hero__mark-heart" />
                <FaHandHoldingHeart className="cp-hero__mark-hand" />
                <svg className="cp-hero__squiggle" viewBox="0 0 220 120" fill="none">
                  <path
                    d="M2 2 C 2 40, 2 70, 40 90 S 150 118, 215 108"
                    stroke="#f2622e"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              <motion.h2
                className="cp-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Children's Parliament
              </motion.h2>
              <motion.p
                className="cp-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Empowering the next generation to advocate for their right to
                quality education and participation in decision-making.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Introduction ================= */}
      <section className="cp-intro">
        <span className="cp-intro__dots" aria-hidden="true" />
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-start g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <div className="cp-eyebrow">
                  <span className="cp-eyebrow__icon">
                    <FaUsers />
                  </span>
                  <span className="cp-eyebrow__label">Child Participation</span>
                </div>
                <span className="cp-eyebrow__dash" />

                <h2 className="cp-heading">
                  A Platform for
                  <br />
                  <span>Young Voices</span>
                </h2>

                <p className="cp-intro__text">
                  We organized a mock parliament involving children from MUKTI
                  pre-school and various government and private schools. This
                  initiative provided a unique opportunity for children to
                  discuss the challenges of equitable education and highlight
                  their right to participation under the UN Convention on the
                  Rights of the Child. Through a democratic process, these
                  initiatives bring zest to the children's learning domain and
                  mentor them as future advocates.
                </p>

                <div className="cp-callout">
                  <span className="cp-callout__icon">
                    <FaBullhorn />
                  </span>
                  <div>
                    <h6>Every child has a voice.</h6>
                    <p>Together, we build a future where every voice is heard.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <img
                  src="/assets/images/Subsections/pc1.jpg"
                  alt="Children participating in the mock parliament"
                  className="cp-intro__image"
                />

                <div className="cp-banner">
                  <span className="cp-banner__icon">
                    <FaUsers />
                  </span>
                  <p>
                    Empowering young minds to speak, participate, and create
                    meaningful change.
                  </p>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Objectives ================= */}
      <section className="cp-approach">
        <span className="cp-approach__dots" aria-hidden="true" />
        <span className="cp-approach__leaf" aria-hidden="true">
          🌿
        </span>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="cp-approach__header"
          >
            <span className="cp-approach__badge">
              <FaBullseye />
            </span>
            <h2 className="cp-heading cp-heading--center">
              Objectives of the <span>Children's Parliament</span>
            </h2>
            <span className="cp-heading__underline" />
            <p className="cp-approach__lead">
              Our goal is to foster an environment of active participation
              and advocacy.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {objectives.map((item) => {
                const Icon = item.icon;
                return (
                  <Col md={6} key={item.title}>
                    <motion.div variants={staggerItem} className="cp-card">
                      <span className="cp-card__icon">
                        <Icon />
                      </span>
                      <div>
                        <h5 className="cp-card__title">{item.title}</h5>
                        <p className="cp-card__text">{item.text}</p>
                      </div>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="cp-cta">
        <Container>
          <motion.div
            className="cp-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="cp-cta__content">
              <span className="cp-cta__icon">
                <FaHandHoldingHeart />
              </span>
              <h2 className="cp-cta__title">
                Empower
                <br />
                <span>Young Leaders</span>
              </h2>
              <span className="cp-cta__underline" />
              <p className="cp-cta__text">
                Your support helps us create more platforms for children to
                voice their opinions and advocate for their rights.
              </p>
              <div className="cp-cta__actions">
                <button
                  type="button"
                  className="cp-btn cp-btn--solid"
                  onClick={openDonation}
                >
                  <FaRegHeart /> Donate
                </button>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>

            <div className="cp-cta__imageWrap">
              <img
                src="/assets/images/Subsections/pc1.jpg"
                alt="Child speaking at the parliament"
                className="cp-cta__image"
              />
              <span className="cp-cta__dots" />
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default ChildrensParliament;